'use client'

import { useEffect, useState } from 'react'

import { useRouter } from 'next/navigation'

import Typography from '@mui/material/Typography'

import classnames from 'classnames'
import { signIn, useSession } from 'next-auth/react'

import { Box, IconButton, InputAdornment, TextField } from '@mui/material'

import axios from 'axios'

import themeConfig from '@configs/themeConfig'

import { useSettings } from '@core/hooks/useSettings'

import CustomButton from '@/components/CustomButton'
import LogoLogin from '@/components/LogoLogin'
import getUrlApiLogin from '@/libs/getUrlApiLogin'
import showToastAlert from '@components/Toast'

const Login = () => {
  const { settings } = useSettings()

  const router = useRouter()
  const { data: session, status } = useSession()

  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [loading, setLoading] = useState(false)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  useEffect(() => {
    if (status === 'authenticated') {
      const redirectTo = '/home'

      router.push(redirectTo)
    }
  }, [status, session, router])

  const clearAllCookies = async () => {
    const cookies = document.cookie.split('; ')

    cookies.forEach(cookie => {
      const [name] = cookie.split('=')

      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/;`
    })

    console.log('Client-side cookies cleared!')

    try {
      await axios.get('/api/clear-cookies')
      console.log('Server-side cookies cleared!')
    } catch (error) {
      console.error('Failed to clear server-side cookies:', error)
    }
  }

  const doLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setLoading(true)
    await clearAllCookies()
    const url = await getUrlApiLogin()

    let isurlpublic = ''

    if (url === (process.env.NEXT_PUBLIC_API_URL as string)) {
      isurlpublic = 'true'
    } else {
      isurlpublic = 'false'
    }

    try {
      const result = await signIn('credentials', {
        redirect: false,
        user: username,
        pass: password,
        isurlpublic: isurlpublic
      })

      if (result?.error) {
        showToastAlert('info', result.error)
      } else {
        router.push(themeConfig.homePageUrl)
      }
    } catch (error) {
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className='flex bs-full justify-center'>
        <div
          className={classnames(
            'flex bs-full items-center justify-center flex-1 min-bs-[100dvh] relative p-6 max-md:hidden',
            {
              'border-ie': settings.skin === 'bordered'
            }
          )}
        >
          <img
            src='/images/icon/logo_bg_white.png'
            alt=''
            className='absolute inset-0 w-full h-full object-cover z-[-1]'
          />
        </div>
        <div className='flex justify-center items-center bs-full bg-backgroundPaper !min-is-full p-6 md:!min-is-[unset] md:p-12 md:is-[480px]'>
          <div className='absolute block sm:hidden block-start-5 inline-start-6'>
            <LogoLogin />
          </div>
          <div className='flex flex-col gap-5 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset]'>
            <div>
              <Typography variant='h5' className='text-center'>
                Selamat datang di
                <strong>{` ${themeConfig.templateName}! 👋🏻`}</strong>
              </Typography>
            </div>

            <form noValidate onSubmit={doLogin} autoComplete='off' className='flex flex-col gap-5'>
              <TextField
                autoFocus
                fullWidth
                label='Username'
                onChange={e => setUsername(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label='Password'
                type={isPasswordShown ? 'text' : 'password'}
                onChange={e => setPassword(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        edge='end'
                        onClick={handleClickShowPassword}
                        onMouseDown={e => e.preventDefault()}
                      >
                        <i className={isPasswordShown ? 'ri-eye-off-line' : 'ri-eye-line'} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%'
                }}
              >
                <CustomButton type='submit' loading={loading} color='primary' fullWidth={true}>
                  Log In
                </CustomButton>
              </Box>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

export default Login
